import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';

interface ProductAtGlanceProps {
  productCode?: string;
  productType: string;
  issuer: string;
  underlyings: string;
  issuePrice: number | null;
  tenorMonths: number | null;
  coupon: number | null;
  denomination: number | null;
  currency: string;
  initialFixing: string | null;
  issueDate: string | null;
  finalFixing: string | null;
  redemptionDate: string | null;
  showIssuer?: boolean;
  couponBarrier?: number | null;
  showCouponBarrier?: boolean;
  couponMemory?: boolean | null;
  showCouponMemory?: boolean;
  showCoupon?: boolean;
  upside: number | null;
  showUpside?: boolean;
  upsideParticipation: number | null;
  showUpsideParticipation?: boolean;
  downside: number | null;
  showDownside?: boolean;
  downsideParticipation: number | null;
  showDownsideParticipation?: boolean;
  showSettlementType?: boolean;
  settlementType: string | null;
  showGrade?: boolean;
  grade: string | null;
  showRecovery?: boolean;
  recovery: string | null;
  showPremium?: boolean;
  premium: number | null;
  showQuotationType?: boolean;
  quotationType: string | null;
  showStrike?: boolean;
  strike: number | null;
  showBasketOptionType?: boolean;
  basketOptionType: string | null;
  showUpperStrike?: boolean;
  upperStrike: number | null;
  showLowerStrike?: boolean;
  showBarrierType?: boolean;
  barrierType: string | null;
  showLowerBarrier?: boolean;
  lowerBarrier: number | null;
  showUpperBarrier?: boolean;
  upperBarrier: number | null;
  showDenomination?: boolean;
  showLeverage?: boolean;
  leverage: number | null;
  showRatio?: boolean;
  ratio: number | null;
  showStopLoss?: boolean;
  stopLoss: number | null;
  showCouponTrigger?: boolean;
  couponTrigger: number | null;
  showCouponFrequency?: boolean;
  couponFrequency: string | null;
  showCouponType?: boolean;
  couponType: string | null;
  showCapitalProtection?: boolean;
  capitalProtection: number | null;
  showCouponStepUp?: boolean;
  couponStepUp: number | null;
  showCouponMinPct?: boolean;
  couponMinPct: number | null;
  showCouponMaxPct?: boolean;
  couponMaxPct: number | null;
  basketTypeUp: number;
  strikeLevel: number;
  bonusLevel: number;
  capLevel: number;
  maxReturn: number;
  atGlanceProps?: Record<string, boolean>;
  showIssuePrice?: boolean;
  tradedNotional: number;
  showTradedNotional?: boolean;
  currentNotional?: number | null;
  showCurrentNotional?: boolean;
}

const formatProductType = (code?: string, type?: string): string => {
  if (code && type) {
    return `${code} – ${type}`;
  }
  return type || 'N/A';
};

export const ProductAtGlance: React.FC<ProductAtGlanceProps> = ({
  productCode,
  productType,
  issuer,
  underlyings,
  issuePrice,
  showIssuePrice = true,
  tradedNotional,
  showTradedNotional,
  currentNotional,
  showCurrentNotional,
  tenorMonths,
  coupon,
  denomination,
  currency,
  initialFixing,
  issueDate,
  finalFixing,
  redemptionDate,
  couponBarrier,
  basketTypeUp,
  upsideParticipation,
  downside,
  downsideParticipation,
  strikeLevel,
  bonusLevel,
  capLevel,
  maxReturn,
  showIssuer = true,
  showCouponBarrier = false,
  couponMemory,
  showCouponMemory = false,
  showCoupon,
  showUpside,
  upside,
  showUpsideParticipation,
  showDownside,
  showDownsideParticipation,
  showSettlementType,
  settlementType,
  showRecovery,
  recovery,
  showGrade,
  grade,
  atGlanceProps = {},
  showPremium,
  premium,
  showQuotationType,
  quotationType,
  showStrike,
  strike,
  showBasketOptionType,
  basketOptionType,
  showUpperStrike,
  upperStrike,
  showLowerStrike,
  showBarrierType,
  barrierType,
  showLowerBarrier,
  lowerBarrier,
  showUpperBarrier,
  upperBarrier,
  showDenomination,
  showLeverage,
  leverage,
  showRatio,
  ratio,
  showStopLoss,
  stopLoss,
  showCouponTrigger,
  couponTrigger,
  showCouponFrequency,
  couponFrequency,
  showCouponType,
  couponType,
  showCapitalProtection,
  capitalProtection,
  showCouponStepUp,
  couponStepUp,
  showCouponMinPct,
  couponMinPct,
  showCouponMaxPct,
  couponMaxPct,
}) => {
  // Helper function to check if a prop should be shown
  // Returns true if the prop is explicitly set to true in atGlanceProps
  const shouldShow = (propName: string): boolean => {
    return atGlanceProps[propName] === true;
  };

  const showCapOrMax = shouldShow('capLevel') || shouldShow('maxReturn');
  const settlementPairedWithCapMax = Boolean(showSettlementType && showCapOrMax);

  const renderCapLevelField = () => (
    <View>
      <Text style={styles.label}>Cap Level (%)</Text>
      <Text style={styles.value}>
        {capLevel == null || capLevel === 0 ? 'NONE' : formatNumber(capLevel)}
      </Text>
    </View>
  );

  const renderMaxReturnField = () => (
    <View style={shouldShow('capLevel') ? styles.stackedField : undefined}>
      <Text style={styles.label}>Max. Return (%)</Text>
      <Text style={styles.value}>
        {maxReturn == null || maxReturn === 0 ? 'NONE' : formatNumber(maxReturn)}
      </Text>
    </View>
  );

  // Helper function to format numeric values safely
  const formatNumber = (value: number | string | null | undefined): string => {
    try {
      if (value == null || value === '' || value === undefined) return '-';

      // Handle different types
      let numValue: number;
      if (typeof value === 'string') {
        const parsed = parseFloat(value);
        if (isNaN(parsed)) return '-';
        numValue = parsed;
      } else if (typeof value === 'number') {
        numValue = value;
      } else {
        // Try to convert to number if it's something else
        const converted = Number(value);
        if (isNaN(converted)) return '-';
        numValue = converted;
      }

      // Validate the number
      if (isNaN(numValue) || !isFinite(numValue)) return '-';

      // Double check it's actually a number before calling toFixed
      if (typeof numValue !== 'number') return '-';

      // Ensure toFixed exists and is a function
      if (typeof numValue.toFixed !== 'function') return '-';

      const isWholeNumber = Math.abs(numValue - Math.round(numValue)) < 1e-9;
      return `${isWholeNumber ? Math.round(numValue) : numValue.toFixed(2)}%`;
    } catch (error) {
      console.warn('Error formatting number:', value, error);
      return '-';
    }
  };

  // Helper function to check if a value is a valid number
  const isValidNumber = (value: number | string | null | undefined): boolean => {
    if (value == null || value === '') return false;

    // Handle string values (common from database)
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') return false;
      const parsed = parseFloat(trimmed);
      return !isNaN(parsed) && isFinite(parsed);
    }

    // Handle number values
    if (typeof value === 'number') {
      return !isNaN(value) && isFinite(value);
    }

    // Try to convert other types
    const converted = Number(value);
    return !isNaN(converted) && isFinite(converted);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  return (
    <View style={[styles.container, Platform.OS === 'web' && { padding: 16, marginBottom: 12 }]}>
      <Text style={styles.sectionTitle}>At a glance</Text>
      <View style={styles.separator} />

      <View style={styles.grid}>
        {/* Row 1 */}
        <View style={styles.row}>
          <View style={styles.item}>
            <Text style={styles.label}>Product type</Text>
            <Text style={styles.value}>{formatProductType(productCode, productType)}</Text>
          </View>
          {showIssuer && (
            <View style={styles.item}>
              <Text style={styles.label}>Issuer</Text>
              <Text style={styles.value}>{issuer}</Text>
            </View>
          )}
        </View>

        {/* Row 2 */}
        <View style={styles.row}>
          <View style={styles.item}>
            <Text style={styles.label}>Underlyings</Text>
            <Text style={styles.valueSmall}>{underlyings || '-'}</Text>
          </View>
        </View>

        {/* Row 3 */}
        <View style={styles.row}>
          {showIssuePrice && (
            <View style={styles.item}>
              <Text style={styles.label}>Issue price</Text>
              <Text style={styles.value}>
                {issuePrice !== null && issuePrice !== undefined && issuePrice > 0
                  ? `${issuePrice.toFixed(2)}%`
                  : '-'}
              </Text>
            </View>
          )}
          <View style={styles.item}>
            <Text style={styles.label}>Tenor</Text>
            <Text style={styles.value}>
              {tenorMonths ? `${tenorMonths} months` : productCode === '2210' ? 'Open End' : '-'}
            </Text>
          </View>
        </View>

        {/* Row 4 - Coupon and Denomination */}
        <View style={styles.row}>
          {showCoupon && (
            <View style={styles.item}>
              <Text style={styles.label}>Coupon</Text>
              <Text style={styles.value}>{coupon ? `${coupon.toFixed(2)}%` : '-'}</Text>
            </View>
          )}
          {showDenomination && (
            <View style={styles.item}>
              <Text style={styles.label}>Denomination</Text>
              <Text style={styles.value}>
                {currency && denomination ? `${currency} ${denomination.toLocaleString()}` : '-'}
              </Text>
            </View>
          )}
        </View>

        {(showTradedNotional || showCurrentNotional) && (
          <View style={styles.row}>
            {showCurrentNotional && (
              <View style={styles.item}>
                <Text style={styles.label}>Current Notional</Text>
                <Text style={styles.value}>
                  {currentNotional != null && Number.isFinite(currentNotional)
                    ? ` ${currentNotional.toLocaleString('en-US')}`
                    : '-'}
                </Text>
              </View>
            )}
            {showTradedNotional && (
              <View style={styles.item}>
                <Text style={styles.label}>Traded Notional</Text>
                <Text style={styles.value}>
                  {tradedNotional ? ` ${tradedNotional.toLocaleString('en-US')}` : '-'}
                </Text>
              </View>
            )}
          </View>
        )}
        <View style={styles.row}>
          {/* Coupon barrier */}
          {showCouponBarrier && (
            <View style={styles.item}>
              <Text style={styles.label}>Coupon Barrier</Text>
              <Text style={styles.value}>
                {couponBarrier ? `${couponBarrier.toFixed(2)}%` : '-'}
              </Text>
            </View>
          )}
          {showCouponMemory && (
            <View style={styles.item}>
              <Text style={styles.label}>Coupon Memory</Text>
              <Text style={styles.value}>{couponMemory ? `Yes` : 'No'}</Text>
            </View>
          )}
        </View>

        {/* Dynamic rows for optional fields - grouped in pairs */}
        {(() => {
          const optionalFields: Array<{
            key: string;
            label: string;
            value: number | string | null | undefined;
            show: boolean;
          }> = [];

          // Build list of fields that should be shown (show if configured, regardless of value)
          if (shouldShow('strikeLevel')) {
            optionalFields.push({
              key: 'strikeLevel',
              label: 'Strike level',
              value: strikeLevel,
              show: true,
            });
          }
          if (shouldShow('bonusLevel')) {
            optionalFields.push({
              key: 'bonusLevel',
              label: 'Bonus level',
              value: bonusLevel,
              show: true,
            });
          }

          // Group fields in pairs (2 per row)
          const rows: Array<typeof optionalFields> = [];
          for (let i = 0; i < optionalFields.length; i += 2) {
            rows.push(optionalFields.slice(i, i + 2));
          }

          return rows.map((rowFields, rowIndex) => (
            <View key={`optional-row-${rowIndex}`} style={styles.row}>
              {rowFields.map(field => (
                <View key={field.key} style={styles.item}>
                  <Text style={styles.label}>{field.label}</Text>
                  <Text style={styles.value}>{formatNumber(field.value)}</Text>
                </View>
              ))}
            </View>
          ));
        })()}
        {/* Row 10 - Dates */}
        <View style={styles.row}>
          <View style={styles.item}>
            <Text style={styles.label}>Initial fixing</Text>
            <View style={styles.dateRow}>
              <View style={[styles.dateIndicator, styles.dateIndicatorInitialFixing]} />
              <Text style={styles.value}>{formatDate(initialFixing)}</Text>
            </View>
          </View>
          <View style={styles.item}>
            <Text style={styles.label}>Issue date</Text>
            <View style={styles.dateRow}>
              <View style={[styles.dateIndicator, styles.dateIndicatorIssueDate]} />
              <Text style={styles.value}>{formatDate(issueDate)}</Text>
            </View>
          </View>
        </View>

        {/* Row 11 - More Dates */}
        <View style={styles.row}>
          <View style={styles.item}>
            <Text style={styles.label}>Final fixing</Text>
            <View style={styles.dateRow}>
              <View style={[styles.dateIndicator, styles.dateIndicatorFinalFixing]} />
              <Text style={styles.value}>{formatDate(finalFixing)}</Text>
            </View>
          </View>
          <View style={styles.item}>
            <Text style={styles.label}>Redemption</Text>
            <View style={styles.dateRow}>
              <View style={[styles.dateIndicator, styles.dateIndicatorRedemption]} />
              <Text style={styles.value}>{formatDate(redemptionDate)}</Text>
            </View>
          </View>
        </View>

        {showUpside && showUpsideParticipation && (
          <View style={styles.row}>
            {showUpside && (
              <View style={styles.item}>
                <Text style={styles.label}>Upside</Text>
                <Text style={styles.value}>{upside ? `${upside}` : '-'}</Text>
              </View>
            )}
            {showUpsideParticipation && (
              <View style={styles.item}>
                <Text style={styles.label}>Upside Participation</Text>
                <Text style={styles.value}>
                  {upsideParticipation !== null && upsideParticipation !== undefined
                    ? `${upsideParticipation.toFixed(2)}%`
                    : '-'}
                </Text>
              </View>
            )}
          </View>
        )}
        {showDownside && showDownsideParticipation && (
          <View style={styles.row}>
            {showDownside && (
              <View style={styles.item}>
                <Text style={styles.label}>Downside</Text>
                <Text style={styles.value}>{downside ? `${downside}` : '-'}</Text>
              </View>
            )}
            {showDownsideParticipation && (
              <View style={styles.item}>
                <Text style={styles.label}>Downside Participation</Text>
                <Text style={styles.value}>
                  {downsideParticipation !== null && downsideParticipation !== undefined
                    ? `${downsideParticipation.toFixed(2)}%`
                    : '-'}
                </Text>
              </View>
            )}
          </View>
        )}
        {(shouldShow('capLevel') || shouldShow('maxReturn')) && (
          <View style={styles.row}>
            <View style={styles.item}>
              {settlementPairedWithCapMax && (
                <>
                  <Text style={styles.label}>Settlement Type</Text>
                  <Text style={styles.value}>{settlementType ? `${settlementType}` : '-'}</Text>
                </>
              )}
            </View>
            <View style={styles.item}>
              {shouldShow('capLevel') && renderCapLevelField()}
              {shouldShow('maxReturn') && renderMaxReturnField()}
            </View>
          </View>
        )}
        {/* Row 7 - Optionals: Settlement Type, Grade, Recovery */}
        {showSettlementType && !settlementPairedWithCapMax && (
          <View style={styles.row}>
            {showSettlementType && (
              <View style={styles.item}>
                <Text style={styles.label}>Settlement Type</Text>
                <Text style={styles.value}>{settlementType ? `${settlementType}` : '-'}</Text>
              </View>
            )}
            {showGrade && (
              <View style={styles.item}>
                <Text style={styles.label}>Grade</Text>
                <Text style={styles.value}>{grade ? `${grade}` : '-'}</Text>
              </View>
            )}
          </View>
        )}
        {showRecovery && (
          <View style={styles.row}>
            {showRecovery && (
              <View style={styles.item}>
                <Text style={styles.label}>Recovery</Text>
                <Text style={styles.value}>{recovery ? `${recovery}` : '-'}</Text>
              </View>
            )}
          </View>
        )}
        {
          <View style={styles.row}>
            {showPremium && (
              <View style={styles.item}>
                <Text style={styles.label}>Premium</Text>
                <Text style={styles.value}>{premium ? `${premium.toFixed(2)}%` : '-'}</Text>
              </View>
            )}
            {showQuotationType && (
              <View style={styles.item}>
                <Text style={styles.label}>Quotation Type</Text>
                <Text style={styles.value}>{quotationType ? `${quotationType}` : '-'}</Text>
              </View>
            )}
          </View>
        }
        {
          <View style={styles.row}>
            {showStrike && (
              <View style={styles.item}>
                <Text style={styles.label}>Strike</Text>
                <Text style={styles.value}>{strike ? `${strike.toFixed(2)}%` : '-'}</Text>
              </View>
            )}
            {showBasketOptionType && (
              <View style={styles.item}>
                <Text style={styles.label}>Basket Option Type Name</Text>
                <Text style={styles.value}>{basketOptionType ? `${basketOptionType}` : '-'}</Text>
              </View>
            )}
          </View>
        }
        {showLowerStrike && showUpperStrike && (
          <View style={styles.row}>
            {showLowerStrike && (
              <View style={styles.item}>
                <Text style={styles.label}>Lower Strike</Text>
                <Text style={styles.value}>{strike ? `${strike.toFixed(2)}%` : '-'}</Text>
              </View>
            )}
            {showUpperStrike && (
              <View style={styles.item}>
                <Text style={styles.label}>Upper Strike </Text>
                <Text style={styles.value}>{upperStrike ? `${upperStrike.toFixed(2)}%` : '-'}</Text>
              </View>
            )}
          </View>
        )}
        {showBarrierType && (
          <View style={styles.row}>
            {showBarrierType && (
              <View style={styles.item}>
                <Text style={styles.label}>Barrier Type</Text>
                <Text style={styles.value}>{barrierType ? `${barrierType}` : '-'}</Text>
              </View>
            )}
          </View>
        )}

        {showUpperBarrier && showLowerBarrier && (
          <View style={styles.row}>
            {showUpperBarrier && (
              <View style={styles.item}>
                <Text style={styles.label}>Upper Barrier</Text>
                <Text style={styles.value}>
                  {upperBarrier ? `${upperBarrier.toFixed(2)}%` : '-'}
                </Text>
              </View>
            )}
            {showLowerBarrier && (
              <View style={styles.item}>
                <Text style={styles.label}>Lower Barrier </Text>
                <Text style={styles.value}>
                  {lowerBarrier ? `${lowerBarrier.toFixed(2)}%` : '-'}
                </Text>
              </View>
            )}
          </View>
        )}
        {showCouponTrigger && showCouponFrequency && (
          <View style={styles.row}>
            {showCouponTrigger && (
              <View style={styles.item}>
                <Text style={styles.label}>Coupon Trigger</Text>
                <Text style={styles.value}>
                  {couponTrigger ? `${couponTrigger.toFixed(2)}%` : '-'}
                </Text>
              </View>
            )}
            {showRatio && (
              <View style={styles.item}>
                <Text style={styles.label}>Coupon Frequency </Text>
                <Text style={styles.value}>{couponFrequency ? `${couponFrequency}` : '-'}</Text>
              </View>
            )}
          </View>
        )}
        {showCouponType && showCapitalProtection && (
          <View style={styles.row}>
            {showCouponType && (
              <View style={styles.item}>
                <Text style={styles.label}>Coupon Type</Text>
                <Text style={styles.value}>{couponType ? `${couponType}` : '-'}</Text>
              </View>
            )}
            {showCapitalProtection && (
              <View style={styles.item}>
                <Text style={styles.label}>Capital Protection </Text>
                <Text style={styles.value}>
                  {capitalProtection ? `${capitalProtection.toFixed(2)}` : '-'}
                </Text>
              </View>
            )}
          </View>
        )}
        {showCouponStepUp && (
          <View style={styles.row}>
            {showCouponStepUp && (
              <View style={styles.item}>
                <Text style={styles.label}>Coupon Step Up</Text>
                <Text style={styles.value}>
                  {couponStepUp ? `${couponStepUp.toFixed(2)}` : '-'}
                </Text>
              </View>
            )}
          </View>
        )}
        {showCouponMinPct && showCouponMaxPct && (
          <View style={styles.row}>
            {showCouponMinPct && (
              <View style={styles.item}>
                <Text style={styles.label}>Coupon Minimum</Text>
                <Text style={styles.value}>
                  {couponMinPct ? `${couponMinPct.toFixed(2)}%` : '-'}
                </Text>
              </View>
            )}
            {showCouponMaxPct && (
              <View style={styles.item}>
                <Text style={styles.label}>Coupon Maximum </Text>
                <Text style={styles.value}>
                  {couponMaxPct ? `${couponMaxPct.toFixed(2)}%` : '-'}
                </Text>
              </View>
            )}
          </View>
        )}
        {showLeverage && showRatio && (
          <View style={styles.row}>
            {showLeverage && (
              <View style={styles.item}>
                <Text style={styles.label}>Leverage</Text>
                <Text style={styles.value}>{leverage ? `${leverage.toFixed(2)}` : '-'}</Text>
              </View>
            )}
            {showRatio && (
              <View style={styles.item}>
                <Text style={styles.label}>Ratio </Text>
                <Text style={styles.value}>{ratio ? `${ratio.toFixed(2)}` : '-'}</Text>
              </View>
            )}
          </View>
        )}
        {showStopLoss && (
          <View style={styles.row}>
            {showStopLoss && (
              <View style={styles.item}>
                <Text style={styles.label}>Stop Loss</Text>
                <Text style={styles.value}>{stopLoss ? `${stopLoss.toFixed(2)}` : '-'}</Text>
              </View>
            )}
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  separator: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginBottom: 16,
  },
  grid: {
    gap: 12,
  },
  row: {
    flexDirection: 'row',
    gap: 16,
  },
  item: {
    flex: 1,
  },
  stackedField: {
    marginTop: 12,
  },
  label: {
    fontSize: 15,
    color: '#757575',
    marginBottom: 4,
  },
  value: {
    fontSize: 14,
    fontWeight: '600',
    color: '#212121',
  },
  valueSmall: {
    fontSize: 12,
    color: '#212121',
    fontWeight: 'bold',
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  dateIndicator: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#212121',
  },
  dateIndicatorInitialFixing: {
    backgroundColor: '#84C6CD',
  },
  dateIndicatorIssueDate: {
    backgroundColor: '#569B98',
  },
  dateIndicatorFinalFixing: {
    backgroundColor: '#44809B',
  },
  dateIndicatorRedemption: {
    backgroundColor: '#00506E',
  },
});
